import { Box, Flex } from '@chakra-ui/react';
import { Switch } from '@mesh.ai/rabbit';
import { useDispatch } from 'react-redux';
import { components } from 'react-select';
import {
  setReviewCreationCalibrationAndDeliverables,
  setReviewCreationCalibrationQuestion,
  setReviewCreationGridDefinition,
  useSelectReviewCreationCalibrationAndDeliverables,
  useSelectReviewCreationGridMetaData,
} from 'reducers/reviewCreationV1Slice';
import { generateRows } from 'screens/admin/Reviews/CalibrationSettings/common/helper';
import { AnswerTypes, AnswerTypesLabel } from 'screens/admin/Reviews/ReviewProcessV1/Steps/Calibration/ScoreSection';
import { TextScoreSettingTable } from 'screens/admin/Reviews/ReviewProcessV1/Steps/Calibration/TextScoreSettingTable';

import { OrderedList, Text } from 'assets/assetsV1/iconComponents';

import { SelectDashed } from 'uiToolkitV1/Select';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const defaultTextAnswerOptions = [
  {
    description: 'Below expectations',
    score: 1,
    displayOrder: 1,
  },
  {
    description: 'Meets expectations',
    score: 2,
    displayOrder: 2,
  },
  {
    description: 'Exceeds expectations',
    score: 3,
    displayOrder: 3,
  },
  {
    description: 'Far exceeds expectations',
    score: 4,
    displayOrder: 4,
  },
  {
    description: 'Outstanding',
    score: 5,
    displayOrder: 5,
  },
];

const defaultNumericOptions = [
  {
    description: '',
    displayOrder: 1,
    score: 1,
  },
  {
    description: '',
    displayOrder: 2,
    score: 2,
  },
  {
    description: '',
    displayOrder: 3,
    score: 3,
  },
  {
    description: '',
    displayOrder: 4,
    score: 4,
  },
  {
    description: '',
    displayOrder: 5,
    score: 5,
  },
];

const SingleValueComp = (props) => {
  const { SingleValue } = components;
  const {
    data: { Icon, label },
  } = props;
  return (
    <SingleValue {...props}>
      <Flex alignItems="center" sx={{ color: '#333' }}>
        <Icon boxSize="18px" mr={2} color="icon.g2" />
        {label}
      </Flex>
    </SingleValue>
  );
};

const OptionComp = (props) => {
  const { Option } = components;
  const {
    data: { Icon, label },
  } = props;
  return (
    <Option {...props}>
      <Flex alignItems="center" sx={{ color: '#333' }}>
        <Icon boxSize="18px" mr={2} color="icon.g2" />
        {label}
      </Flex>
    </Option>
  );
};

const getOptionObject = (score) => ({
  description: '',
  displayOrder: score,
  score,
});

const TextOptionsSettings = ({ updateGridHandler }) => {
  const { calibrationQuestion } = useSelectReviewCreationCalibrationAndDeliverables();
  const { answerOptions } = calibrationQuestion ?? {};
  const dispatch = useDispatch();

  const onChangeHandler = (newAnswerOptions) => {
    dispatch(
      setReviewCreationCalibrationQuestion({
        answerOptions: newAnswerOptions,
      })
    );
    updateGridHandler(newAnswerOptions);
  };

  return (
    <Box px={1}>
      <TextScoreSettingTable scoreSetting={answerOptions} onChange={onChangeHandler} />
    </Box>
  );
};

const NumericOptionSettings = ({ updateGridHandler }) => {
  const { calibrationQuestion } = useSelectReviewCreationCalibrationAndDeliverables();
  const { answerOptions } = calibrationQuestion ?? {};
  const dispatch = useDispatch();

  const options = [3, 4, 5, 6, 7, 8, 9, 10].map((i) => ({
    value: i,
    label: i,
  }));

  const onChangeHandler = (selectedOption) => {
    const newAnswerOptions = Array.from({ length: selectedOption.value }, (_, i) => getOptionObject(i + 1));
    dispatch(
      setReviewCreationCalibrationQuestion({
        answerOptions: newAnswerOptions,
      })
    );
    updateGridHandler(newAnswerOptions);
  };

  return (
    <Flex alignItems="center" mt="14px">
      <TextBodyMedium minW="40%">Max Score:</TextBodyMedium>
      <Box flexGrow={1}>
        <SelectDashed
          options={options}
          value={options.filter((o) => o.value === answerOptions[answerOptions.length - 1]?.score)[0]}
          handleChange={onChangeHandler}
          placeholder={undefined}
          getOptionValue={undefined}
          getOptionLabel={undefined}
          selectRef={undefined}
        />
      </Box>
    </Flex>
  );
};

const OverallScore = () => {
  const dispatch = useDispatch();
  const { calibrationQuestion, isCalibrationQuestionDisabled } = useSelectReviewCreationCalibrationAndDeliverables();
  const { answerType, answerOptions } = calibrationQuestion ?? {};

  const { rowsCount, columnsCount } = useSelectReviewCreationGridMetaData();

  const ratingTypeOptions = Object.keys(AnswerTypes).map((option) => ({
    value: option,
    label: AnswerTypesLabel[option],
    Icon: option === AnswerTypes.DROP_DOWN ? Text : OrderedList,
  }));

  // eslint-disable-next-line no-shadow
  const calculateGridByMaxValue = ({ newMaxScore, rowsCount, columnsCount }) => {
    // @ts-expect-error wrong function signature
    const newGrid = generateRows({ rows: rowsCount, cols: columnsCount, maxScore: newMaxScore });
    return newGrid;
  };

  const updateGridHandler = (newAnswerOptions) => {
    const maxValue =
      newAnswerOptions && newAnswerOptions.length > 0 ? newAnswerOptions[newAnswerOptions.length - 1].score : 0;
    const newGrid = calculateGridByMaxValue({ rowsCount, columnsCount, newMaxScore: maxValue });
    dispatch(setReviewCreationGridDefinition(newGrid));
  };

  const onChangeHandler = (selectedOption) => {
    const newAnswerOptions =
      selectedOption.value === AnswerTypes.DROP_DOWN ? defaultTextAnswerOptions : defaultNumericOptions;
    dispatch(
      setReviewCreationCalibrationQuestion({
        answerType: selectedOption.value,
        answerOptions: newAnswerOptions,
      })
    );
    // Grid creation yet to be implemented
    // updateGridHandler(newAnswerOptions);
  };

  const onCalibrationQuestionDisabledChange = (value: boolean) => {
    if (value && (answerOptions?.length === 0 || !answerOptions)) {
      dispatch(
        setReviewCreationCalibrationQuestion({
          answerType: AnswerTypes.DROP_DOWN,
          answerOptions: defaultTextAnswerOptions,
        })
      );
    }
    dispatch(setReviewCreationCalibrationAndDeliverables({ isCalibrationQuestionDisabled: !value }));
  };

  return (
    <Flex borderRadius="8px" flexDir="column" border="4px solid #E7EBEF" bg="white">
      <Flex flexDir="column" py={2} px={3}>
        <Flex
          flexDir="column"
          pb={4}
          borderBottom="1px solid"
          borderColor={isCalibrationQuestionDisabled ? 'transparent' : 'border.CFD7DF'}
        >
          <TextBodyMedium bold>Overall score</TextBodyMedium>
          <Flex justifyContent="space-between" alignItems="center">
            <TextLabel color="text.g2">
              Overall score is the final aggregated score, reflecting the scores received from all reviewers.
            </TextLabel>
            <Switch checked={!isCalibrationQuestionDisabled} onCheckedChange={onCalibrationQuestionDisabledChange} />
          </Flex>
        </Flex>
      </Flex>
      {!isCalibrationQuestionDisabled && (
        <Box py={2} px={3}>
          <Flex alignItems="center">
            <TextBodyMedium mr={2}>Configure how the overall score will be calculated and displayed</TextBodyMedium>
            <TooltipInfoV1 title="If rating scales for each element in the templates are different, all scores will be first rebased, and then the overall score will be calculated based on the scale selected in this section." />
          </Flex>
          <Box mt="14px">
            <Flex alignItems="center">
              <TextBodyMedium minW="40%">Score Type:</TextBodyMedium>
              <Flex flexDir="column" width="100%">
                <SelectDashed
                  options={ratingTypeOptions}
                  value={answerType ? ratingTypeOptions.filter((o) => o.value === answerType)[0] : ratingTypeOptions[0]}
                  handleChange={onChangeHandler}
                  components={{ Option: OptionComp, SingleValue: SingleValueComp }}
                  placeholder={undefined}
                  getOptionValue={undefined}
                  getOptionLabel={undefined}
                  selectRef={undefined}
                />
              </Flex>
            </Flex>
            <Box ml="auto" w="60%">
              {answerType === AnswerTypes.DROP_DOWN && (
                <TextLabel color="text.g2" mt={1}>
                  Selecting this option will display the specified text to users based on the defined score ranges.
                </TextLabel>
              )}
            </Box>

            {answerType === AnswerTypes.DROP_DOWN && <TextOptionsSettings updateGridHandler={updateGridHandler} />}
            {answerType === AnswerTypes.NUMBER_RATING && (
              <NumericOptionSettings updateGridHandler={updateGridHandler} />
            )}
          </Box>
        </Box>
      )}
    </Flex>
  );
};

export { OverallScore };
